import ArrowRight from '@betfan/icons/linear/Arrows/ArrowRight.svg?react';
import CheckCircle from '@betfan/icons/linear/EssentionalUI/CheckCircle.svg?react';
import LockKeyhole from '@betfan/icons/linear/Security/LockKeyholeMinimalistic.svg?react';
import logoOfBanks from 'assets/images/banks_logo_grid.webp';
import cn from 'classnames';
import { DSButton } from 'designSystem';
import { motion, useAnimation } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { animationDuration } from './utils';

interface VerificationMojeIdProps {
	verifyMethodMojeId: () => void;
}

//Set this variable to check if image was loaded to make sure of real element height
const minimalHeightOfSecondScreen = 500;

export const VerificationMojeId: React.FC<VerificationMojeIdProps> = ({ verifyMethodMojeId }) => {
	const { t } = useTranslation();
	const [expandMojeId, setExpandMojeId] = useState(false);
	const controls = useAnimation();
	const verifySecondScreenRef = useRef<HTMLDivElement>(null);
	const startAnimation = (expandMojeId: boolean) => {
		const height = verifySecondScreenRef?.current?.clientHeight ?? 0;
		if (expandMojeId && height) {
			if (height < minimalHeightOfSecondScreen) {
				startAnimation(expandMojeId);
			} else {
				controls.start({ height: height });
			}
		}
	};
	useEffect(() => {
		startAnimation(expandMojeId);
	}, [expandMojeId]);

	return (
		<motion.div
			animate={controls}
			transition={{ duration: animationDuration, ease: 'linear' }}
			className={cn(
				'text-paragraph-sm relative bg-white rounded-16',
				'outline outline-[3px] outline-offset-[-1.50px] outline-accent-400',
				'inline-flex flex-col justify-start items-start self-stretch',
			)}
		>
			<div className="px-1 py-0.5 left-4 top-[-10px] absolute bg-accent-400 rounded-2 inline-flex justify-center items-center h-5">
				<span className="justify-start text-paragraph-xs font-semibold uppercase text-gray-900">
					{t('verificationMethods_mojeId_fastest')}
				</span>
			</div>
			<div className={'relative overflow-hidden w-full h-full '}>
				<motion.div
					initial={{ opacity: 1 }}
					animate={{ opacity: expandMojeId ? 0 : 1 }}
					transition={{ duration: animationDuration, ease: 'linear' }}
					className={'w-full  left-0 top-0 right-0'}
				>
					<div className={'p-4'}>
						<h2 className="text-h3 text-gray-900 font-bold my-0 pb-2">{t('verificationMethods_mojeId_header')}</h2>
						<div className={'text-gray-800 font-medium'}>{t('verificationMethods_mojeId_subtitle')}</div>
						<div className={'grayscale opacity-70'}>
							<img
								onClick={() => {
									if (!expandMojeId) {
										setExpandMojeId(true);
									}
									verifyMethodMojeId();
								}}
								src={logoOfBanks}
								className="cursor-pointer max-w-full max-h-full justify-center w-full object-cover px-1 py-1"
								alt="mBank PKOBP Santander PEKAO ING Millennium Credit Agricole Alior BNP VeloBank Banki Spółdzielcze BOŚ Inteligo Kasa Stefczyka"
							/>
						</div>
						<div className="font-medium text-gray-800 my-0 mb-4">{t('verificationMethods_mojeId_noDocument')}</div>
						<DSButton
							cyTestRef="mojeId-button"
							size="medium"
							color={'primary'}
							className="w-full outline-0"
							onClick={() => {
								if (!expandMojeId) {
									setExpandMojeId(true);
								}
								verifyMethodMojeId();
							}}
						>
							{t('verificationMethods_mojeId_button')}
						</DSButton>
					</div>
				</motion.div>
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: expandMojeId ? 1 : 0, zIndex: expandMojeId ? '1' : '-1' }}
					transition={{ duration: animationDuration, ease: 'linear' }}
					className={' absolute left-0 top-0 right-0'}
					ref={verifySecondScreenRef}
				>
					<div className={'p-4'}>
						<h2 className="text-h3 text-gray-900 font-bold my-0 pb-2">{t('verificationMethods_mojeId_header')}</h2>
						<div className={'text-gray-900 font-semibold'}>{t('verificationMethods_mojeId_expanded_subheader')}</div>
						<div className="text-gray-800 font-medium my-0 flex flex-row w-full pt-0 mt-2">
							<div className="mr-1 m-0">
								<CheckCircle className="text-success-500 w-4 h-4" />
							</div>
							<p className="my-0">{t('verificationMethods_mojeId_expanded_subtitle')}</p>
						</div>

						<img
							onClick={() => {
								if (!expandMojeId) {
									setExpandMojeId(true);
								}
								verifyMethodMojeId();
							}}
							src={logoOfBanks}
							className="cursor-pointer max-w-full max-h-full justify-center w-full object-cover px-1 py-1"
							alt="mBank PKOBP Santander PEKAO ING Millennium Credit Agricole Alior BNP VeloBank Banki Spółdzielcze BOŚ Inteligo Kasa Stefczyka"
						/>

						<div className="text-gray-800 font-medium flex flex-row w-full mb-1">
							<div className="mr-1 m-0">
								<CheckCircle className="text-success-500 w-4 h-4" />
							</div>
							<p className="my-0">{t('verificationMethods_mojeId_expanded_login')}</p>
						</div>
						<div className="text-gray-800 font-medium flex flex-row w-full">
							<div className="mr-1 m-0">
								<CheckCircle className="text-success-500 w-4 h-4" />
							</div>
							<p className="my-0">
								{t('verificationMethods_mojeId_expanded_accountNumber')}
								<b>{t('verificationMethods_mojeId_expanded_accountNumber_bold')}</b>
							</p>
						</div>
						<div className="w-full h-[2px] bg-primary-100 my-3" />
						<div className="text-gray-800 font-medium flex flex-row w-full mb-4">
							<div className="mr-1 m-0">
								<LockKeyhole className="text-success-500 w-4 h-4" />
							</div>
							<p className="my-0">
								{t('verificationMethods_mojeId_expanded_security')}
								<b>{t('verificationMethods_mojeId_expanded_security_bold')}</b>
							</p>
						</div>
						<DSButton
							cyTestRef="mojeId-button"
							size="medium"
							color={'primary'}
							className="w-full outline-0"
							onClick={() => {
								if (!expandMojeId) {
									setExpandMojeId(true);
								}
								verifyMethodMojeId();
							}}
						>
							<span>
								{t('verificationMethods_mojeId_expanded_button')}
								<ArrowRight className="text-primary-400 w-5 h-5 mx-[6px] mt-[-2px]" />
							</span>
						</DSButton>
					</div>
				</motion.div>
			</div>
		</motion.div>
	);
};
